import React, { useEffect } from "react";
import dayjs from "dayjs";
import { Redirect } from "react-router-dom";
import styles from "./PatientDashboard.module.scss";
import { useQueryParam } from "hooks/useQueryParam";
import { useCurrentPatient } from "./hooks/useCurrentPatient";
import { UpcomingAppointment } from "./UpcomingAppointment";
import { ErrorToast } from "./ErrorToast";
import { EyeHealthChecklist } from "./EyeHealthChecklist";
import { RecentPurchases } from "./RecentPurchases";
import { Subscriptions } from "./Subscriptions";
import { MyAccount } from "./MyAccount";
import { Layout } from "features/layout/Layout";
import { Navbar } from "components/Navbar";
import { portalLogin } from "features/routing/paths";
import { useOrders } from "./hooks/useOrders";
import { useSubscriptions } from "./hooks/useSubscriptions";
import { useCurrentAppointment } from "./hooks/useCurrentAppointment";

const getGreeting = () => {
  const hour = dayjs().hour();

  if (hour < 12) {
    return "Morning";
  } else if (hour < 17) {
    return "Afternoon";
  } else {
    return "Evening";
  }
};

declare let getCLXUrl: string;
export const PatientDashboard: React.FC = () => {
  const [login, setlogin] = useQueryParam("login", false);
  const { sessionStatus, currentPatient, fetchCurrentPatient } =
    useCurrentPatient();
  const { fetchCurrentAppointment } = useCurrentAppointment();
  const { orders } = useOrders(currentPatient?.id);
  const { subscriptions } = useSubscriptions(currentPatient?.id);
  const shouldHideTopPrescriptionsOnMobile = orders?.length === 0;
  const shouldHideFooterCtasOnDesktop =
    orders === undefined ||
    subscriptions === undefined ||
    orders.length > 0 ||
    subscriptions.length > 0
      ? false
      : true;
  
  if(login === "true") {
    setlogin("false");
    location.reload();
  }

  // Check to see if the user is logged in when dashboard first mounts.
  useEffect(() => {
    if (sessionStatus === "uninitialized") {
      fetchCurrentPatient();
    }
  }, [fetchCurrentPatient, sessionStatus]);

  // Ensure appointment details are refreshed whenever the user lands on portal home.
  useEffect(() => {
    if (sessionStatus === "logged-in") {
      fetchCurrentAppointment();
    }
  }, [fetchCurrentAppointment, sessionStatus]);

  if (sessionStatus === "logged-out" || sessionStatus === "expired") {
    return <Redirect to={portalLogin()} />;
  }

  if (sessionStatus === "uninitialized") return null;
  return (
    <Layout
      showBanner={true}
      scheduleBanner={true}
      bannerTypes={["global", "patient_portal", "maintenance"]}
      navbar={
        <>
          <Navbar
            office={currentPatient?.homeOffice}
            className={styles.myAccount}
          >
            <MyAccount />
          </Navbar>
        </>
      }
    >
      <ErrorToast />
      <div className={styles.container}>
        <div className={styles.patientGreeting}>
          Good {getGreeting()},
          <h1 id="chk-patientName" className={styles.patientName}>
            {currentPatient?.firstName?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
            {currentPatient?.lastName?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
          </h1>
        </div>
        <div className={styles.columns}>
          <UpcomingAppointment officeId={currentPatient?.homeOffice?.id} />
          <div>
            <EyeHealthChecklist />
            <div
              className={`${styles.ctasBottom} ${
                shouldHideTopPrescriptionsOnMobile
                  ? styles.hideOnMobileWhenNoOrders
                  : ""
              }`}
            >
              Need access to your prescriptions?{" "}
              <br className={styles.mobileOnly} />
              <a
                href={getCLXUrl}
                className={styles.inlineLink}
                target="_blank"
                rel="noreferrer"
              >
                View&nbsp;prescriptions
              </a>
            </div>
          </div>
        </div>
        <div className={styles.recentPurchasesContainer}>
          {currentPatient && <Subscriptions patient={currentPatient} />}
        </div>
        <div className={styles.recentPurchasesContainer}>
          {currentPatient && (
            <RecentPurchases patient={currentPatient} showCtas={false} />
          )}
        </div>
        <div
          className={`${styles.footerCtas} ${
            shouldHideFooterCtasOnDesktop
              ? styles.hideOnDesktopWhenNoActivity
              : ""
          }`}
        >
          <div className={styles.footerCta}>
            <div
              className={
                shouldHideTopPrescriptionsOnMobile
                  ? styles.hideOnMobileWhenNoOrders
                  : undefined
              }
            >
              Something doesn't look right?{" "}
              <br className={styles.mobileOnly} />
              <a
                href={"https://locations.myeyedr.com/search"}
                className={styles.footerInlineLink}
                target="_blank"
                rel="noreferrer"
              >
                Contact your local office
              </a>
            </div>
          </div>
          <div className={styles.footerCta}>
            Need access to your prescriptions?{" "}
            <br className={styles.mobileOnly} />
            <a
              href={getCLXUrl}
              className={styles.footerInlineLink}
              target="_blank"
              rel="noreferrer"
            >
              View prescriptions
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};
